import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, FormControlLabel, FormLabel, Grid, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { Questionnaire } from 'fhir/r4b';
import { FC, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoundedButton } from 'src/components/RoundedButton';
import {
  FORM_PLACEMENT_LABELS,
  FORM_PLACEMENTS,
  FormPlacement,
  getFormPlacement,
  makePracticeManagedUrl,
  PRACTICE_MANAGED_QUESTIONNAIRE_BASE_VERSION,
  practiceManagedQuestionnaireToFhir,
  withFormPlacement,
} from 'utils/lib/helpers/practice-managed-questionnaires';
import { slugify } from 'utils/lib/helpers/slugify';
import {
  PracticeManagedQuestionnaire,
  PracticeManagedQuestionnaireItem,
} from 'utils/lib/types/data/practice-managed-questionnaires/practice-managed-questionnaire.types';
import { itemsReducer } from '../questionnaire.reducer';
import { QuestionnaireItemEditor } from './QuestionnaireItemEditor';
import { QuestionnaireJsonPreview } from './QuestionnaireJsonPreview';
import { QuestionnairePreview } from './QuestionnairePreview';
import { QuestionnaireTestDialog } from './QuestionnaireTestDialog';

interface QuestionnaireBuilderProps {
  initial?: PracticeManagedQuestionnaire;
  onSave: (questionnaire: PracticeManagedQuestionnaire) => Promise<void>;
  isSaving: boolean;
}

// All slug derivation (linkIds and the canonical url) caps at 60 chars.
const toSlug = (text: string): string => slugify(text, { maxLength: 60 });

// The canonical url/name are identity: existing QuestionnaireResponses reference the
// questionnaire by canonical URL, so editing (even retitling) an existing resource must
// preserve them. Only brand-new questionnaires derive a slug from the title.
const makeCanonicalFields = (
  input: { initial: PracticeManagedQuestionnaire } | { title: string }
): { name: string; url: string } => {
  if ('initial' in input) {
    const { initial } = input;
    return { name: initial.name, url: initial.url };
  } else {
    const { title } = input;
    const slug = toSlug(title);
    const url = makePracticeManagedUrl(slug);
    return { name: slug, url };
  }
};

// link ids are derived from the content of the of the item and must be unique amongst all items
function ensureUniqueLinkIds(
  items: PracticeManagedQuestionnaireItem[],
  seen = new Set<string>(),
  pageSlug?: string
): PracticeManagedQuestionnaireItem[] {
  return items.map((item) => {
    let base = item.linkId;
    if (!base && item.text) {
      const textSlug = toSlug(item.text);
      if (item.type === 'group') {
        base = textSlug ? `${textSlug}-page` : '';
      } else if (item.type === 'display') {
        base = pageSlug ? `${pageSlug}-${textSlug}-text` : `${textSlug}-text`;
      } else {
        base = pageSlug ? `${pageSlug}-${textSlug}` : textSlug;
      }
    }
    if (!base) base = `item-${crypto.randomUUID().slice(0, 6)}`;

    let linkId = base;
    let counter = 2;
    while (seen.has(linkId)) {
      linkId = `${base}-${counter}`;
      counter++;
    }
    seen.add(linkId);

    const childPageSlug = item.type === 'group' ? toSlug(item.text || '') : undefined;
    return {
      ...item,
      linkId,
      item: item.item ? ensureUniqueLinkIds(item.item, seen, childPageSlug) : undefined,
    };
  });
}

const FORM_PLACEMENT_HELP: Record<FormPlacement, string> = {
  'visit-details': "Shown on the visit's Visit Details page.",
  screening: 'Staff fill it out on the Screening Questions page, below the built-in screening questions.',
  questionnaires: "Shown on the chart's Clinical Forms page, together with the patient's earlier responses.",
};

export const QuestionnaireBuilder: FC<QuestionnaireBuilderProps> = ({ initial, onSave, isSaving }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [placement, setPlacement] = useState<FormPlacement>(getFormPlacement(initial));
  const [items, dispatch] = useReducer(itemsReducer, initial?.item || []);
  const [titleError, setTitleError] = useState(false);
  const [pagesError, setPagesError] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  const [currentPreviewPageIndex, setCurrentPreviewPageIndex] = useState(0);
  const [previewCompleted, setPreviewCompleted] = useState(false);

  const navigate = useNavigate();

  const { questionnaire, fhirQuestionnaire, jsonPreview } = useMemo(() => {
    const canonicalFields = makeCanonicalFields(initial ? { initial } : { title });
    const status: Questionnaire['status'] = initial ? initial.status : 'active';
    const version = initial?.version ?? PRACTICE_MANAGED_QUESTIONNAIRE_BASE_VERSION;
    const metaTags = withFormPlacement(initial?.meta?.tag, placement);

    const questionnaire: PracticeManagedQuestionnaire = {
      resourceType: 'Questionnaire',
      status,
      ...(initial?.id && { id: initial.id }),
      ...canonicalFields,
      version,
      title,
      ...(description && { description }),
      item: ensureUniqueLinkIds(items),
      ...(metaTags.length > 0 && { meta: { tag: metaTags } }),
    };

    const fhirQuestionnaire = practiceManagedQuestionnaireToFhir(structuredClone(questionnaire), true);
    const jsonPreview = JSON.stringify(fhirQuestionnaire, null, 2);

    return { questionnaire, fhirQuestionnaire, jsonPreview };
  }, [initial, title, description, placement, items]);

  const handleSave = async (): Promise<void> => {
    let error = false;
    if (!title) {
      setTitleError(true);
      error = true;
    }

    if (questionnaire.item.length === 0) {
      setPagesError(true);
      error = true;
    }

    if (error) return;

    await onSave(questionnaire);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 160px)' }}>
        <Box sx={{ flex: '1 1 50%', minWidth: 0, overflow: 'auto' }}>
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="h4" sx={{ mb: 1.5, color: '#0F347C' }}>
              Questionnaire Properties
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setTitleError(false);
                  }}
                  error={titleError}
                  helperText={titleError ? 'Title is required' : undefined}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel id="form-placement-label">Where answers appear</FormLabel>
                <RadioGroup
                  aria-labelledby="form-placement-label"
                  value={placement}
                  onChange={(e) => setPlacement(e.target.value as FormPlacement)}
                >
                  {FORM_PLACEMENTS.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2">{FORM_PLACEMENT_LABELS[value]}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {FORM_PLACEMENT_HELP[value]}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, ...(pagesError && { borderColor: 'error.main' }) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="h4" sx={{ color: '#0F347C' }}>
                Pages ({items.length})
              </Typography>
              <RoundedButton
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  dispatch({ type: 'ADD_PAGE' });
                  setPagesError(false);
                }}
              >
                Add Page
              </RoundedButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Each page becomes a separate screen. Add items inside pages using the + button.
            </Typography>

            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No pages yet. Click "Add Page" to create your first page.
              </Typography>
            )}

            {items.map((item: PracticeManagedQuestionnaireItem) => (
              <QuestionnaireItemEditor key={item._key} item={item} dispatch={dispatch} />
            ))}
          </Paper>
          {pagesError && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5, ml: 1.5 }}>
              At least one page is required
            </Typography>
          )}

          {/* Spacer for floating buttons */}
          <Box sx={{ height: 64 }} />
        </Box>

        {/* Right column: Form Preview + JSON Preview — independent scroll */}
        <Box
          sx={{
            flex: '1 1 50%',
            overflow: 'auto',
          }}
        >
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h4" sx={{ color: '#0F347C' }}>
                Form Preview
              </Typography>
              <RoundedButton
                size="medium"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => setTestDialogOpen(true)}
                disabled={!questionnaire.item?.length}
              >
                Test Form
              </RoundedButton>
            </Box>
            <QuestionnairePreview
              questionnaire={fhirQuestionnaire}
              setCurrentPageIndex={setCurrentPreviewPageIndex}
              currentPageIndex={currentPreviewPageIndex}
              completed={previewCompleted}
              setCompleted={setPreviewCompleted}
              previewMode={'ui-only'}
            />
          </Paper>
          <QuestionnaireTestDialog
            open={testDialogOpen}
            onClose={() => setTestDialogOpen(false)}
            questionnaire={fhirQuestionnaire}
          />

          <QuestionnaireJsonPreview json={jsonPreview} />
        </Box>
      </Box>

      {/* Floating action bar. `left` reads the --admin-sidebar-width custom property set by
          AdminSidebar so the bar spans the content column instead of the full viewport. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 'var(--admin-sidebar-width, 0px)',
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 1.5,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          zIndex: 1200,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <RoundedButton
          variant="outlined"
          size="medium"
          onClick={() => navigate('/admin/questionnaires')}
          disabled={isSaving}
        >
          Cancel
        </RoundedButton>
        <RoundedButton variant="contained" size="medium" loading={isSaving} onClick={() => handleSave()}>
          Save Questionnaire
        </RoundedButton>
      </Box>
    </Box>
  );
};
